import React, { useCallback, useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { ExpandableConfig } from 'antd/es/table/interface';
import { Column, DataTable } from '@ydistri/ds';
import { CustomExportRunResponse, CustomExportRunType } from '@ydistri/api-sdk';
import { formatDateTime } from '@ydistri/generic-utils';
import { useTemplateOrCalculation } from '../../../../hooks/useTemplateOrCalculation';
import LoadingIndicator from '../../../../components/global/LoadingIndicator/LoadingIndicator';
import { useGetCustomExportRunsQuery } from '../apiCalculationExport';
import { defaultCustomExportListQuery, sortCustomExportByCreatedAtDesc } from '../customExportLib';
import CustomExportRunItemTable from './CustomExportRunItemTable';
import CustomExportStatusBadge from './CustomExportStatusBadge';

const customExportRunTypeLabels: Record<CustomExportRunType, string> = {
  [CustomExportRunType.New]: 'New',
  [CustomExportRunType.RetryErrors]: 'Retry errors',
  [CustomExportRunType.SkipErrors]: 'Skip errors',
};

const CustomExportRunTable: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculation();
  const { data, isFetching, isError } = useGetCustomExportRunsQuery({
    calculationId: templateOrCalculation.id,
    query: defaultCustomExportListQuery,
  });

  const runs = useMemo(() => sortCustomExportByCreatedAtDesc(data?.data ?? []), [data?.data]);

  const columns = useMemo<ColumnsType<CustomExportRunResponse>>(
    () => [
      {
        title: 'Run ID',
        dataIndex: 'id',
        key: 'id',
        width: '7rem',
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: '9rem',
        render: (value: CustomExportRunType) => customExportRunTypeLabels[value],
      },
      {
        title: 'Latest',
        dataIndex: 'isLast',
        key: 'isLast',
        width: '7rem',
        render: (value: boolean) => (value ? 'Yes' : 'No'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '10rem',
        render: (value: CustomExportRunResponse['status']) => (
          <CustomExportStatusBadge status={value} />
        ),
      },
      {
        title: 'File download',
        dataIndex: 'fileForDownloadUrl',
        key: 'fileForDownloadUrl',
        width: '9rem',
        render: (value?: string | null) =>
          value ? (
            <a href={value} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : (
            'N/A'
          ),
      },
      {
        title: 'Error message',
        dataIndex: 'errorMessage',
        key: 'errorMessage',
        render: (value?: string | null) => value ?? 'N/A',
      },
      {
        title: 'Original run',
        dataIndex: 'originalCustomExportRunId',
        key: 'originalCustomExportRunId',
        width: '10rem',
        render: (value?: number | null) => value ?? 'N/A',
      },
      {
        title: 'Created at',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '12rem',
        render: (value: string) => formatDateTime(value),
      },
    ],
    [],
  );

  const expandableConfig = useMemo<ExpandableConfig<CustomExportRunResponse>>(
    () => ({
      expandedRowRender: record => (
        <CustomExportRunItemTable customExportRunId={record.id} latest={record.isLast} />
      ),
    }),
    [],
  );

  const rowKeyProvider = useCallback((record: CustomExportRunResponse) => record.id, []);

  if (isFetching && !data) {
    return <LoadingIndicator />;
  }

  if (isError) {
    return <div>Failed to load custom export runs.</div>;
  }

  if (runs.length === 0) {
    return (
      <Column $padding={1} $gap="1rem">
        <div>No custom export runs found.</div>
      </Column>
    );
  }

  return (
    <Column $padding={1}>
      <DataTable<CustomExportRunResponse>
        id="custom-export-runs"
        dataSource={runs}
        columns={columns}
        rowKey={rowKeyProvider}
        loading={isFetching}
        showHeader={true}
        showBorder={true}
        expandable={expandableConfig}
      />
    </Column>
  );
};

export default CustomExportRunTable;
